/* eslint-disable */
import React, { useEffect, useMemo } from 'react';
import { useUiStore } from '../../stores/uiStore';
import { usePortalPermissions, useCanSeeMailIntel } from '../../hooks/usePortalPermissions';
import { useDeadlineNotifications } from '../../hooks/useDeadlineNotifications';
import { useNotifications } from '../../hooks/useNotifications';
import { useDevCounts } from '../../hooks/useDevCounts';
import { useNotificationsStore } from '../../stores/notificationsStore';
import { portalKeyFromTab } from '../../constants/portals';
import DeadlinesPanel from './panels/DeadlinesPanel';
import NotificationsPanel from './panels/NotificationsPanel';
import ShellDrawer from './panels/ShellDrawer';
import { useShellStore } from './shellStore';
import { useCondense, useMediaMax } from './useCondense';
import { BrandMarkDefs } from './BrandMark';
import { Ico } from './ShellIcons';
import AppBar from './AppBar';
import GenericBar from './GenericBar';
import DevsFilterBar from './devs/DevsFilterBar';
import OriginalsFilterBar from './originals/OriginalsFilterBar';
import OrdersFilterBar from './orders/OrdersFilterBar';
import BasedOnPeek from './cards/BasedOnPeek';
import DataFilterBar, { DATA_TABS } from './data/DataFilterBar';
import sh from './shell.module.css';
import './portalsV3.css';

// ============================================================
// ShellRoot — Shell v3 (CLB-012). Mounted by App.js instead of AppHeader +
// AppFilterBar when the flag is on. Owns: the token root (.hubRoot), the app
// bar, the portal's filter bar, the deadlines panel, condense-on-scroll, the
// one-open-popover contract (document click · Escape) and the chrome height
// App.js writes into --app-header-height.
// ============================================================

const FZ_GAP = 8; // the floating card's gap above and below the bar + rail (--fz-gap, CLB-080 S7)
const DEV_BOARD_VIEWS = new Set(['board', undefined, null, '']);

export default function ShellRoot(props) {
  const { user, role, tab, setTab, perm, showToast, scopedDevsForDeadline, isDeadlineAdmin, reloadDevs, setShowGlobalSearch, setGlobalSearch, onBackToPortals,
    activePortal, devView, activeDevs, devSettings, filteredDevsList, filteredIgnoringStatusList, setShowDevForm, setShowAdd, catalog, myProfile, roleLabel, onLogout, canSettings, setShowShortcuts } = props;

  const open = useShellStore((s) => s.open);
  const toggleOpen = useShellStore((s) => s.toggleOpen);
  const closeAll = useShellStore((s) => s.closeAll);
  const setChrome = useShellStore((s) => s.setChrome);
  const condensed = useShellStore((s) => s.condensed);
  const cardH = useShellStore((s) => s.filterBarH + s.railH);
  const clearPeek = useShellStore((s) => s.clearPeek);
  const setActive = useShellStore((s) => s.setActive);
  useEffect(() => { setActive(true); return () => setActive(false); }, [setActive]);
  const toggleHamburgerDrawer = useUiStore((s) => s.toggleHamburgerDrawer);
  const setNotifDrawer = useNotificationsStore((s) => s.setShowDrawer);
  const { data: ppData } = usePortalPermissions();
  const isAdmin = role === 'admin' || !!ppData?.isSuperAdmin;
  const canSeeMailIntel = useCanSeeMailIntel(role);
  const { totalCount: deadlineCount } = useDeadlineNotifications(scopedDevsForDeadline);
  const { unreadCount: notifCount } = useNotifications();
  const portalKey = portalKeyFromTab(tab);
  const isDevBoard = portalKey === 'developments' && DEV_BOARD_VIEWS.has(devView);
  const isItems = portalKey === 'catalog' && tab === 'catalog' && !!catalog;
  const isData = DATA_TABS.has(tab);
  const { data: devCounts } = useDevCounts(portalKey === 'developments');
  // S6 — the bar a portal published for this screen (QC · Sales · Projects by
  // portal key; a Database by its tab), merged over the identity row.
  const published = useShellStore((s) => s.bars[portalKey || tab] || null);

  const myDesignerNames = useMemo(() => [myProfile?.my_designer_name, ...(Array.isArray(myProfile?.designer_names) ? myProfile.designer_names : [])].filter(Boolean), [myProfile]);
  const phone = useMediaMax(599);
  const toTop = useCondense(true);
  // MAIL-1: the Mail portal draws its own status strip + sub header (the prototype), so no shell bar.
  const hasBar = (!!portalKey && portalKey !== 'mail') || isData;
  useEffect(() => { setChrome({ appBarH: phone ? 48 : 56, filterBarH: hasBar ? 48 + 2 * FZ_GAP : 0 }); }, [phone, hasBar, isDevBoard, setChrome]); // railH is the Rail's own (CLB-064)

  // One open popover: a click anywhere outside a [data-shell-pop] closes it; Escape too.
  useEffect(() => {
    const onClick = (e) => { if (e.target.closest && e.target.closest('[data-shell-pop]')) return; closeAll(); clearPeek(true); };
    const onKey = (e) => { if (e.key === 'Escape') { closeAll(); clearPeek(true); } };
    document.addEventListener('click', onClick);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('click', onClick); document.removeEventListener('keydown', onKey); };
  }, [closeAll, clearPeek]);

  const onPrimary = (action) => {
    if (action === 'newDev') { setTab('developments'); setShowDevForm && setShowDevForm(true); }
    if (action === 'newItem') { setTab('catalog'); setShowAdd && setShowAdd(true); }
  };
  const chromeProps = useMemo(() => ({
    deadlineCount, notifCount,
    onOpenDeadlines: () => toggleOpen('dead'),
    onOpenNotifications: () => toggleOpen('notif'),
    onOpenMenu: () => { closeAll(); toggleHamburgerDrawer(); },
  }), [deadlineCount, notifCount, toggleOpen, closeAll, setNotifDrawer, toggleHamburgerDrawer]);

  return (
    <div className={`${sh.hubRoot} ${condensed ? sh.condensed : ''}`} data-shell-v3 data-condensed={condensed ? '1' : undefined}>
      <BrandMarkDefs />
      <AppBar
        user={user} role={role} tab={tab} setTab={setTab} showToast={showToast}
        deadlineCount={deadlineCount} notifCount={notifCount}
        onToggleDeadlines={() => toggleOpen('dead')} deadlinesOpen={open === 'dead'} onToggleNotifications={() => toggleOpen('notif')} notifOpen={open === 'notif'}
        setShowGlobalSearch={setShowGlobalSearch} setGlobalSearch={setGlobalSearch}
        onBackToPortals={onBackToPortals} onPrimary={onPrimary}
      />
      {hasBar && !isDevBoard ? <div className={sh.fzBack} style={{ height: cardH }} aria-hidden="true" /> : null}
      {isDevBoard ? (
        <DevsFilterBar
          user={user} role={role} perm={perm} devs={activeDevs} devSettings={devSettings} counts={devCounts}
          filteredDevsList={filteredDevsList} filteredIgnoringStatusList={filteredIgnoringStatusList}
          scopedDevsForDeadline={scopedDevsForDeadline} setShowDevForm={setShowDevForm} showToast={showToast} myProfile={myProfile}
          condensed={condensed} chromeProps={chromeProps}
        />
      ) : portalKey === 'orders' ? (
        <OrdersFilterBar condensed={condensed} chromeProps={chromeProps} />
      ) : isItems ? (
        <OriginalsFilterBar user={user} perm={perm} tab={tab} setTab={setTab} catalog={catalog} condensed={condensed} chromeProps={chromeProps} showToast={showToast} />
      ) : isData ? (
        <DataFilterBar tab={tab} setTab={setTab} published={published} condensed={condensed} chromeProps={chromeProps} />
      ) : hasBar ? (
        <GenericBar portalKey={portalKey} tab={tab} setTab={setTab} role={role} isAdmin={isAdmin} canSeeMailIntel={canSeeMailIntel} condensed={condensed} chromeProps={chromeProps} published={published} />
      ) : null}
      <button type="button" className={`${sh.toTop} ${toTop ? sh.on : ''}`} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} aria-label="Back to top"><Ico name="upload" />Top</button>
      <BasedOnPeek />
      <DeadlinesPanel open={open === 'dead'} onClose={closeAll} devs={scopedDevsForDeadline} isAdmin={!!isDeadlineAdmin} myDesignerNames={myDesignerNames} reloadDevs={reloadDevs} showToast={showToast} onGoBoard={() => setTab('developments')} />
      <NotificationsPanel open={open === 'notif'} onClose={closeAll} onGoProfile={() => setTab('my_profile')} />
      <ShellDrawer user={user} myProfile={myProfile} roleLabel={roleLabel} tab={tab} setTab={setTab} isAdmin={isAdmin} canSettings={!!canSettings} onLogout={onLogout} onBackToPortals={onBackToPortals} onOpenShortcuts={() => setShowShortcuts && setShowShortcuts(true)} />
    </div>
  );
}
